import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { firstValueFrom, filter, take, timeout, catchError, of } from 'rxjs';
import { User } from '@angular/fire/auth';
import { PlatformService } from '@app/core/platform/platform.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { ProjectAccessService } from '@app/core/services/project-access';
import { isAdminUser } from '../admin/admin.config';
import { AuthService } from '../services/auth.service';

/**
 * Access is decided by ProjectAccessService and NOWHERE ELSE.
 *
 * This guard used to resolve membership three different ways and treat
 * user_ids.includes() as ownership — it is not. It also required a partner to
 * already hold a ticket, which locked out every partner staffed at quote time,
 * before any ticket exists. That was the "stuck".
 */
export const projectMemberGuard: CanActivateFn = async (route) => {
  const auth = inject(AuthService);
  const platform = inject(PlatformService);
  const router = inject(Router);
  const snack = inject(SnackbarService);
  const access = inject(ProjectAccessService);

  if (platform.isServer) return true;

  const id = route.paramMap.get('id') ?? '';
  if (!id) {
    router.navigateByUrl('/');
    return false;
  }

  const user = await firstValueFrom(
    auth.user$.pipe(
      filter((u): u is User | null => u !== undefined),
      take(1),
      timeout(4000),
      catchError(() => of(null)),
    ),
  );

  if (!user) {
    snack.showInfo('Please log in to view this project.');
    router.navigateByUrl('/login');
    return false;
  }

  if (isAdminUser(user.email, user.uid)) return true;

  const result = await access.resolve(id, user.uid, user.email);
  if (result.can.canView) return true;

  snack.showError("You don't have access to this project.");
  router.navigateByUrl('/');
  return false;
};